import type { ElementType } from 'react';
import {
  HomeOutlined,
  GroupOutlined,
  ShoppingBagOutlined,
  ScienceOutlined,
  MonetizationOnOutlined,
  SettingsOutlined,
  PersonAddOutlined,
  InfoOutlined,
  FactCheckOutlined,
  ReceiptOutlined,
  EventAvailableOutlined,
  LocalShippingOutlined,
  PercentOutlined,
  CallSplitOutlined,
  WaterDropOutlined,
  VerifiedOutlined,
  AccountBalanceWalletOutlined,
  InsertChartOutlined,
  GridViewOutlined,
} from '@mui/icons-material';

// ── react-icons ────────────────────────────────────────────────────────────────
import { TiGroup } from 'react-icons/ti';
import { GiBoxUnpacking, GiSecretBook, GiCoffeeBeans, GiNotebook } from 'react-icons/gi';
import { AiOutlineAudit } from 'react-icons/ai';
import { LuUserCheck, LuPackagePlus, LuCalendarPlus, LuPackageSearch } from 'react-icons/lu';
import { MdContactMail, MdOutlineRequestQuote, MdContactPhone, MdFactCheck, MdOutlinePriceChange } from 'react-icons/md';
import { FaFlaskVial, FaFileLines } from 'react-icons/fa6';
import { BiSolidDuplicate } from 'react-icons/bi';
import { GrDocumentUser } from 'react-icons/gr';
import { TbPackages, TbPackageExport, TbCalendarTime,TbPackageImport  } from 'react-icons/tb';
import { HiMiniUserGroup } from 'react-icons/hi2';
import { GoGraph } from 'react-icons/go';
import { colors } from './colors';
import type { RouteItem, BreadcrumbCrumb, TrailCrumb, StoredCrumb, Department } from '../screens/admin/types';
import {
  AdminRoutes,
  CRMRoutes,
  HomeRoutes,
  DashboardRoutes,
  PurchseRoutes,
  ProductionRoutes,
  CommercialRoutes,
  AuditreportsRoutes,
  FinanceRoutes,
} from '../config/routes';
import { launchpadStrings,commonStrings } from '../components/StringBundle';
import { SidebarDeptDef } from '../screens/admin/types';

// --- Icon sizes ---

/** Icon size for department items in the sidebar rail (px). */
export const DEPT_ICON_SIZE = 16;

/** Icon size for module cards inside the flyout panel (px). */
export const MODULE_ICON_SIZE = 14;

// --- Department colours ---

export interface DeptColors {
  accent: string;
  tint: string;
  csh: string;
}

export const DEPT_COLORS: Record<string, DeptColors> = {
  home:       { accent: colors.steelBlue,  tint: colors.steelBlueTint,  csh: colors.steelBlueCsh  },
  dashboard:  { accent: colors.teal,       tint: colors.tealTint,       csh: colors.tealCsh       },
  crm:        { accent: colors.rose,       tint: colors.roseTint,       csh: colors.roseCsh       },
  commercial: { accent: colors.sienna,     tint: colors.siennaTint,     csh: colors.siennaCsh     },
  purchase:   { accent: colors.saffron,    tint: colors.saffronTint,    csh: colors.saffronCsh    },
  pq:         { accent: colors.azure,      tint: colors.azureTint,      csh: colors.azureCsh      },
  finance:    { accent: colors.violet,     tint: colors.violetTint,     csh: colors.violetCsh     },
  audit:      { accent: colors.emerald,    tint: colors.emeraldTint,    csh: colors.emeraldCsh    },
  admin:      { accent: colors.graphite,   tint: colors.graphiteTint,   csh: colors.graphiteCsh   },
};

/** Fallback colours when a department key is unknown. */
export const FALLBACK_DEPT_COLORS: DeptColors = DEPT_COLORS.admin;

export const MODULE_ICONS: Array<[string, ElementType]> = [
  ['home',               HomeOutlined],
  ['lead',               PersonAddOutlined],
  ['account',            LuUserCheck],
  ['contact',            MdContactMail],
  ['master sample',      GiSecretBook],
  ['alternative',        BiSolidDuplicate],
  ['customer sample',    GrDocumentUser],
  ['sample information', InfoOutlined],
  ['sample request',     FaFlaskVial],
  ['pss',                FaFlaskVial],
  ['pre-shipment',       FactCheckOutlined],
  ['pre shipment',       FactCheckOutlined],
  ['quotation',          MdOutlineRequestQuote ],
  ['quote',              MdOutlineRequestQuote],
  ['qli',                FaFileLines],
  ['validity',           EventAvailableOutlined],
  ['purchase order',     LuPackagePlus],
  ['order line',         TbPackages],
  ['order dispatch',     TbPackageExport],
  ['order',              LuPackagePlus],
  ['forecast capacity',  TbCalendarTime],
  ['soft schedule',      TbCalendarTime],
  ['schedule',           LuCalendarPlus],
  ['debit note',         ReceiptOutlined],
  ['mrin',               TbPackageImport],
  ['supplier',           HiMiniUserGroup],
  ['tax',                PercentOutlined],
  ['packing',            LuPackageSearch],
  ['dispatch',           LocalShippingOutlined],
  ['allocation',         CallSplitOutlined],
  ['gc required',        GiCoffeeBeans],
  ['daily rate',         GiNotebook],
  ['rate',               GiNotebook],
  ['enquiry',            MdContactPhone],
  ['blend',              MdFactCheck],
  ['certif',             VerifiedOutlined],
  ['batch',              GoGraph],
  ['cost',               MdOutlinePriceChange],
  ['finance',            AccountBalanceWalletOutlined],
  ['audit',              InsertChartOutlined],
  ['report',             InsertChartOutlined],
  ['gc',                 GiCoffeeBeans],
  ['sample',             WaterDropOutlined],
  ['user',               GroupOutlined],
  ['setting',            SettingsOutlined],
];

export function getModuleIcon(label: string): ElementType {
  const key = (label || '').toLowerCase();
  const match = MODULE_ICONS.find(([pattern]) => key.includes(pattern));
  return match ? match[1] : GridViewOutlined;
}

const s = launchpadStrings;

export const SIDEBAR_DEPT_DEFS: SidebarDeptDef[] = [
  { key: 'home',       name: s.sidebar.home,              Icon: HomeOutlined,           routes: HomeRoutes         as RouteItem[] },
  { key: 'dashboard',  name: s.sidebar.dashboard,         Icon: GridViewOutlined,       routes: DashboardRoutes    as RouteItem[] },
  { key: 'crm',        name: s.crm.name,                  Icon: TiGroup,                routes: CRMRoutes          as RouteItem[] },
  { key: 'commercial', name: s.commercial.name,           Icon: LocalShippingOutlined,         routes: CommercialRoutes   as RouteItem[] },
  { key: 'purchase',   name: s.purchase.name,             Icon: ShoppingBagOutlined,    routes: PurchseRoutes      as RouteItem[] },
  { key: 'pq',         name: s.productionAndQuality.name, Icon: ScienceOutlined,        routes: ProductionRoutes   as RouteItem[] },
  { key: 'finance',    name: s.finance.name,              Icon: MonetizationOnOutlined, routes: FinanceRoutes      as RouteItem[] },
  { key: 'audit',      name: s.auditReports.name,         Icon: AiOutlineAudit,         routes: AuditreportsRoutes as RouteItem[] },
  { key: 'admin',      name: s.admin.name,                Icon: SettingsOutlined,       routes: AdminRoutes        as RouteItem[] },
];

export function getFilteredDepartments(role: string | undefined): Department[] {
  const roleStr = role as string;
  const result: Department[] = [];
  for (const def of SIDEBAR_DEPT_DEFS) {
    const items = def.routes?.filter((r) => r.roles?.includes(roleStr));
    if (items.length > 0) {
      result.push({
        key:    def.key,
        name:   def.name,
        Icon:   def.Icon,
        items,
        colors: DEPT_COLORS[def.key] ?? FALLBACK_DEPT_COLORS,
      });
    }
  }
  return result;
}

export const normPath = (p: string | undefined): string =>
  (p || '').replace(/\/+$/, '') || '/';

// ─── Breadcrumb path utilities ────────────────────────────────────────────────

const KNOWN_SEGMENTS = new Set<string>([
  'create', 'Create', 'edit', 'view', 'detail', 'Detail', 'delete',
  'add', 'Add', 'contacts', 'sample-item', 'billingAddress', 'shippingAddress',
  'billing', 'shipping', 'dispatch', 'quote', 'sample-request',
]);

export function isIdSegment(seg: string): boolean {
  if (!seg || KNOWN_SEGMENTS.has(seg)) return false;
  if (/^\d+$/.test(seg)) return true;
  if (/^\d/.test(seg))   return true;
  if (seg.length >= 5 && /\d/.test(seg)) return true;
  return false;
}

const WORD_EXPAND: Record<string, string> = {
  Info: 'Information', Addr: 'Address', Dept: 'Department', Qty: 'Quantity', No: 'Number',
};

export function toTitleCase(seg: string): string {
  const parts = seg.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/-/g, ' ').split(' ');
  for (let i = 0; i < parts.length; i++) {
    const t = parts[i].charAt(0).toUpperCase() + parts[i].slice(1);
    parts[i] = WORD_EXPAND[t] || t;
  }
  return parts.join(' ');
}

/** Singularizes the last word of a page label, e.g. "Blend Sheets" -> "Blend Sheet". */
export function singularize(label: string): string {
  return label.replace(/\w+$/, (word) => {
    if (word.endsWith('ies')) return word.slice(0, -3) + 'y';        // Categories -> Category
    if (/(ch|sh|s|x|z)es$/i.test(word)) return word.slice(0, -2);    // Dispatches -> Dispatch
    if (word.endsWith('s') && !word.endsWith('ss')) return word.slice(0, -1); // Leads -> Lead
    return word; // already singular (or an acronym like "GC")
  });
}

export function paramsFromPath(path: string): { ids?: string[] } {
  const segs = (path || '').split('/');
  const ids: string[] = [];
  for (const s of segs) {
    if (s && (/^\d/.test(s) || (s.length >= 5 && /\d/.test(s)))) ids.push(s);
  }
  return ids.length ? { ids } : {};
}

export function splitDestination(to: string): { path: string; queryParams: Record<string, string> } {
  const [path, search = ''] = (to || '').split('?');
  const queryParams: Record<string, string> = {};
  for (const [k, v] of new URLSearchParams(search)) {
    queryParams[k] = v;
  }
  return { path, queryParams };
}


export function normalizePathname(pathname: string): string {
  return (pathname || '').split('/').map(seg => isIdSegment(seg) ? ':id' : seg).join('/');
}

/** Hydrates a slim StoredCrumb (from sessionStorage) back to a full TrailCrumb.
 *  Derived fields are recomputed here and never persisted. */
export function expandCrumb(c: StoredCrumb): TrailCrumb {
  const { path, queryParams } = splitDestination(c.to);
  return {
    label:      c.label,
    to:         c.to,
    key:        c.to,
    id:         c.to,
    title:      c.label,
    pagePath:   c.pagePath,
    deptKey:    c.deptKey ?? null,
    kind:       c.kind,
    tab:        c.tab ?? null,
    state:      c.state || {},
    path,
    params:     paramsFromPath(path),
    queryParams,
  };
}

/** Assigns kind ('module' | 'page' | 'action') to each crumb by index. */
export function tagKinds(list: Omit<BreadcrumbCrumb, 'kind'>[]): BreadcrumbCrumb[] {
  return list.map((item, i) => ({
    ...item,
    kind: (i === 0 ? 'module' : i === 1 ? 'page' : 'action') as BreadcrumbCrumb['kind'],
  }));
}

/** Returns the first segment after fromIndex that is not ':id'. */
export function findNextNonId(rest: string[], fromIndex: number): string | undefined {
  for (let j = fromIndex + 1; j < rest.length; j++) {
    if (rest[j] !== ':id') return rest[j];
  }
  return undefined;
}

export const getInitials = (name: string = ''): string => {
  const words = name.split(' ');
  let initials = '';
  for (let i = 0; i < words.length && i < 2; i++) {
    initials += words[i][0] ?? '';
  }
  return initials.toUpperCase();
};

/** Delay (ms) before a hovered rail item opens its flyout — a brief/accidental hover never opens it. */
export const FLYOUT_OPEN_DELAY_MS = 200;

/** Delay (ms) before the active flyout closes after the cursor leaves it — lets the cursor travel between the rail item and its flyout, or briefly cross another item, without closing. */
export const FLYOUT_CLOSE_DELAY_MS = 300;

export function getInstance() {
  const url = window.location.hostname;
  return url.includes('dev') ? commonStrings.development : url.includes('qa') ? commonStrings.qa : url.includes('beta') ? commonStrings.production : commonStrings.development
}
